import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Spinner } from 'react-bootstrap';
import { useLoadCompanyDirectoryFullQuery } from '../app/api';
import { useIsMobile } from './utils';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#c0392b',
};

/* HR org chart.
 *
 * A hierarchy diagram, not a data chart: there is no magnitude, identity or
 * polarity to encode, so nodes are deliberately UNIFORM. Colouring people by
 * department or depth would imply a data encoding that isn't there, and would
 * bury the one thing the reader is actually looking for. Position carries the
 * structure; ink carries the text.
 *
 * The only colour that means anything is the reserved status hue for a
 * terminated employee, and it ships with an icon + the word "Terminated" rather
 * than colour alone.
 *
 * Two views of the same tree: a top-down diagram on wide screens, and an
 * indented list on narrow ones. The list is not a lesser fallback — it's the
 * linear, screen-reader-friendly reading of the same data, which a purely
 * spatial diagram can't provide.
 */

const BOARD_LABEL = 'Board of Directors';

// Diagram geometry.
const NODE_W = 190;
const H_GAP = 14;        // between sibling subtrees
const V_GAP = 30;        // vertical run of the connector between levels
const LINE = '1px';      // connectors stay recessive, like grid lines

function initials(first, last) {
  return ((first || '').trim().charAt(0) + (last || '').trim().charAt(0)).toUpperCase() || '?';
}

const fullName = (p) => `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.final_email || '—';

/** Build the reporting forest from flat directory rows.
 *
 * Returns { boardRoots, orphanRoots, byId, counts }. Two root sets on purpose:
 * people flagged `reports_to_board` are the real top of the chart, while anyone
 * with no manager and no Board flag is *unplaced* — surfaced separately rather
 * than silently promoted to the top, which would misrepresent the org.
 */
function buildForest(rows) {
  const byId = new Map(rows.map(p => [p.id, p]));
  const children = new Map();
  const boardRoots = [];
  const orphanRoots = [];

  for (const p of rows) {
    const mgr = p.manager_id;
    // A manager_id pointing at someone missing from this result set (e.g. a
    // terminated manager while "show terminated" is off) would otherwise drop
    // that whole subtree off the page. Treat it as unplaced instead.
    if (mgr && byId.has(mgr)) {
      if (!children.has(mgr)) children.set(mgr, []);
      children.get(mgr).push(p);
    } else if (p.reports_to_board) {
      boardRoots.push(p);
    } else {
      orphanRoots.push(p);
    }
  }

  const byName = (a, b) => fullName(a).localeCompare(fullName(b));
  for (const list of children.values()) list.sort(byName);
  boardRoots.sort(byName);
  orphanRoots.sort(byName);

  // Depth of the deepest chain, for the summary line.
  const depthOf = (p, seen = new Set()) => {
    if (seen.has(p.id)) return 0;           // cycle guard: A→B→A can't hang the render
    seen.add(p.id);
    const kids = children.get(p.id) || [];
    return kids.length ? 1 + Math.max(...kids.map(k => depthOf(k, new Set(seen)))) : 1;
  };
  const maxDepth = boardRoots.length ? Math.max(...boardRoots.map(r => depthOf(r))) : 0;

  return {
    boardRoots, orphanRoots, children, byId,
    counts: { total: rows.length, placed: rows.length - orphanRoots.length, maxDepth },
  };
}

/** One person card. Uniform by design — see the note at the top of this file. */
function PersonCard({ person, reportCount, collapsed, onToggle, compact }) {
  const terminated = person.is_active === false;
  return (
    <div
      title={`${fullName(person)}${person.title ? ` — ${person.title}` : ''}${person.final_email ? `\n${person.final_email}` : ''}`}
      style={{
        width: compact ? undefined : NODE_W,
        background: 'white',
        border: `1px solid ${brand.softGray}`,
        borderLeft: `3px solid ${terminated ? brand.charcoal : brand.tealBlue}`,
        borderRadius: 8,
        padding: '0.5rem 0.6rem',
        boxShadow: '0 1px 4px rgba(10,41,71,0.06)',
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        minWidth: 0,
        opacity: terminated ? 0.75 : 1,
      }}
    >
      {person.photo_url ? (
        <img
          src={person.photo_url} alt=""
          style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
        />
      ) : (
        <div style={{
          width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
          background: brand.softGray, color: brand.deepBlue,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.7rem', fontWeight: 700,
        }}>
          {initials(person.first_name, person.last_name)}
        </div>
      )}

      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          color: brand.deepBlue, fontWeight: 700, fontSize: '0.78rem',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {fullName(person)}
        </div>
        {person.title && (
          <div style={{
            color: brand.charcoal, fontSize: '0.7rem', lineHeight: 1.25,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {person.title}
          </div>
        )}
        {/* Status never rides on colour alone. */}
        {terminated && (
          <div style={{ color: brand.charcoal, fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            <i className="bi bi-person-dash-fill me-1" />Terminated
          </div>
        )}
      </div>

      {reportCount > 0 && (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${reportCount} report${reportCount === 1 ? '' : 's'} under ${fullName(person)}`}
          style={{
            flexShrink: 0, border: `1px solid ${brand.softGray}`, background: brand.softGray,
            color: brand.deepBlue, borderRadius: 12, minWidth: 30, height: 22,
            fontSize: '0.68rem', fontWeight: 800, cursor: 'pointer', padding: '0 0.3rem',
          }}
        >
          {collapsed ? `+${reportCount}` : '−'}
        </button>
      )}
    </div>
  );
}
PersonCard.propTypes = {
  person: PropTypes.object.isRequired,
  reportCount: PropTypes.number.isRequired,
  collapsed: PropTypes.bool,
  onToggle: PropTypes.func,
  compact: PropTypes.bool,
};

/** Top-down subtree: card, a vertical stem, then a horizontal rail joining
 *  children. Pure CSS — no layout library, and it prints. */
function DiagramNode({ person, childMap, collapsedIds, onToggle }) {
  const kids = childMap.get(person.id) || [];
  const collapsed = collapsedIds.has(person.id);
  const showKids = kids.length > 0 && !collapsed;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto' }}>
      <PersonCard
        person={person}
        reportCount={kids.length}
        collapsed={collapsed}
        onToggle={() => onToggle(person.id)}
      />

      {showKids && (
        <>
          {/* stem down from the parent */}
          <div style={{ width: LINE, height: V_GAP / 2, background: brand.softGray }} />
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            {kids.map((k, i) => (
              <div key={k.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto', padding: `0 ${H_GAP / 2}px` }}>
                {/* Half-width rails so the horizontal line spans only between
                    the first and last child, never dangling past the ends. */}
                <div style={{ display: 'flex', width: '100%', height: LINE }}>
                  <div style={{ flex: 1, background: i === 0 ? 'transparent' : brand.softGray }} />
                  <div style={{ flex: 1, background: i === kids.length - 1 ? 'transparent' : brand.softGray }} />
                </div>
                <div style={{ width: LINE, height: V_GAP / 2, background: brand.softGray }} />
                <DiagramNode person={k} childMap={childMap} collapsedIds={collapsedIds} onToggle={onToggle} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
DiagramNode.propTypes = {
  person: PropTypes.object.isRequired,
  childMap: PropTypes.instanceOf(Map).isRequired,
  collapsedIds: PropTypes.instanceOf(Set).isRequired,
  onToggle: PropTypes.func.isRequired,
};

/** Indented reading of the same tree. Mobile layout, and the linear view a
 *  spatial diagram can't give a screen reader. */
function ListNode({ person, childMap, depth, collapsedIds, onToggle }) {
  const kids = childMap.get(person.id) || [];
  const collapsed = collapsedIds.has(person.id);
  return (
    <li style={{ listStyle: 'none', marginLeft: depth === 0 ? 0 : '1rem', paddingLeft: depth === 0 ? 0 : '0.75rem', borderLeft: depth === 0 ? 'none' : `1px solid ${brand.softGray}` }}>
      <div style={{ padding: '0.25rem 0' }}>
        <PersonCard
          person={person}
          reportCount={kids.length}
          collapsed={collapsed}
          onToggle={() => onToggle(person.id)}
          compact
        />
      </div>
      {kids.length > 0 && !collapsed && (
        <ul style={{ paddingLeft: 0, margin: 0 }}>
          {kids.map(k => (
            <ListNode key={k.id} person={k} childMap={childMap} depth={depth + 1}
              collapsedIds={collapsedIds} onToggle={onToggle} />
          ))}
        </ul>
      )}
    </li>
  );
}
ListNode.propTypes = {
  person: PropTypes.object.isRequired,
  childMap: PropTypes.instanceOf(Map).isRequired,
  depth: PropTypes.number.isRequired,
  collapsedIds: PropTypes.instanceOf(Set).isRequired,
  onToggle: PropTypes.func.isRequired,
};

export default function HROrgChart() {
  const isMobile = useIsMobile();
  const [showTerminated, setShowTerminated] = useState(false);
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());
  const [forceList, setForceList] = useState(false);

  const { data: rows = [], isLoading, isError, error, refetch } =
    useLoadCompanyDirectoryFullQuery(showTerminated ? { includeTerminated: true } : undefined);

  const forest = useMemo(() => buildForest(rows), [rows]);
  const asList = isMobile || forceList;

  const toggle = (id) => setCollapsedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const collapseAll = () => {
    // Only people who actually have reports are collapsible.
    setCollapsedIds(new Set([...forest.children.keys()]));
  };

  const headerButton = (label, icon, onClick, active) => (
    <button
      type="button" onClick={onClick}
      style={{
        border: `1px solid ${active ? brand.goldenrod : 'rgba(255,255,255,0.35)'}`,
        background: active ? brand.goldenrod : 'transparent',
        color: active ? brand.deepBlue : 'white',
        borderRadius: 8, padding: '0.35rem 0.7rem', fontSize: '0.8rem', fontWeight: 700,
        cursor: 'pointer', whiteSpace: 'nowrap',
      }}
    >
      <i className={`bi bi-${icon} me-1`} />{label}
    </button>
  );

  let body;
  if (isLoading) {
    body = <div className="text-center p-5"><Spinner style={{ color: brand.tealBlue }} /></div>;
  } else if (isError) {
    body = (
      <Alert variant="danger" className="py-2">
        Couldn&apos;t load the directory{error?.status ? ` (${error.status})` : ''}. The chart below would
        otherwise look empty, which isn&apos;t the same thing.
        <div><button type="button" className="btn btn-sm btn-outline-danger mt-2" onClick={refetch}>Try again</button></div>
      </Alert>
    );
  } else if (rows.length === 0) {
    body = <div style={{ color: brand.charcoal, fontSize: '0.9rem' }}>The directory is empty.</div>;
  } else if (forest.boardRoots.length === 0 && forest.orphanRoots.length === 0) {
    body = <div style={{ color: brand.charcoal, fontSize: '0.9rem' }}>No reporting relationships have been set yet.</div>;
  } else {
    const renderRoots = (roots) => (asList ? (
      <ul style={{ paddingLeft: 0, margin: 0 }}>
        {roots.map(p => (
          <ListNode key={p.id} person={p} childMap={forest.children} depth={0}
            collapsedIds={collapsedIds} onToggle={toggle} />
        ))}
      </ul>
    ) : (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: H_GAP, paddingBottom: '0.5rem' }}>
        {roots.map(p => (
          <DiagramNode key={p.id} person={p} childMap={forest.children}
            collapsedIds={collapsedIds} onToggle={toggle} />
        ))}
      </div>
    ));

    body = (
      <>
        {forest.boardRoots.length > 0 && (
          <div style={{
            background: 'white', border: `1px solid ${brand.softGray}`, borderRadius: 12,
            padding: isMobile ? '0.75rem' : '1rem',
            // A wide chart scrolls horizontally rather than squeezing nodes to
            // illegibility.
            overflowX: 'auto',
          }}>
            {/* The Board is the root of the chart but not a directory entry, so
                it's drawn as a labelled anchor rather than a person card. */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: asList ? 'flex-start' : 'center', minWidth: 'min-content' }}>
              <div style={{
                background: brand.deepBlue, color: 'white', borderRadius: 8,
                borderBottom: `3px solid ${brand.goldenrod}`,
                padding: '0.5rem 0.9rem', fontWeight: 800, fontSize: '0.85rem', whiteSpace: 'nowrap',
              }}>
                <i className="bi bi-bank me-2" style={{ color: brand.goldenrod }} />{BOARD_LABEL}
              </div>
              {!asList && <div style={{ width: LINE, height: V_GAP / 2, background: brand.softGray }} />}
              <div style={{ marginTop: asList ? '0.5rem' : 0 }}>{renderRoots(forest.boardRoots)}</div>
            </div>
          </div>
        )}

        {forest.orphanRoots.length > 0 && (
          <div style={{
            background: 'white', border: `1px solid ${brand.softGray}`, borderRadius: 12,
            padding: isMobile ? '0.75rem' : '1rem', marginTop: '1rem', overflowX: 'auto',
          }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
              <span style={{ color: brand.deepBlue, fontWeight: 800, fontSize: '0.9rem' }}>
                <i className="bi bi-question-circle me-1" style={{ color: brand.goldenrod }} />
                Not placed on the chart ({forest.orphanRoots.length})
              </span>
              <span style={{ color: brand.charcoal, fontSize: '0.78rem' }}>
                No manager set, and not reporting to the Board. Set a manager in Manage Directory to place them.
              </span>
            </div>
            <div style={{ minWidth: 'min-content' }}>{renderRoots(forest.orphanRoots)}</div>
          </div>
        )}
      </>
    );
  }

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: isMobile ? '0.75rem' : '1.5rem' }}>
      {/* Header */}
      <div style={{
        background: brand.deepBlue, borderRadius: 12,
        padding: isMobile ? '1.25rem' : '1.75rem 2rem',
        marginBottom: isMobile ? '1rem' : '1.5rem', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: 0, right: 0, width: 300, height: '100%', background: 'linear-gradient(135deg, transparent 40%, rgba(0,105,166,0.3))', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: 3, background: brand.goldenrod }} />
        <div style={{ display: 'flex', gap: '1rem', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', minWidth: 0, flex: 1 }}>
            <div style={{ background: 'rgba(208,155,44,0.15)', border: `2px solid ${brand.goldenrod}`, borderRadius: 12, padding: '0.75rem', flexShrink: 0 }}>
              <i className="bi bi-diagram-3-fill" style={{ color: brand.goldenrod, fontSize: '1.75rem' }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 2, margin: '0 0 4px' }}>
                HR Portal
              </p>
              <h3 style={{ color: 'white', fontWeight: 800, margin: '0 0 4px', fontSize: isMobile ? '1.25rem' : undefined }}>Org Chart</h3>
              {!isLoading && !isError && (
                <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                  {forest.counts.placed} of {forest.counts.total} placed
                  {forest.counts.maxDepth > 0 && ` · ${forest.counts.maxDepth} level${forest.counts.maxDepth === 1 ? '' : 's'} deep`}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', width: isMobile ? '100%' : 'auto' }}>
            {headerButton('Expand all', 'arrows-expand', () => setCollapsedIds(new Set()), false)}
            {headerButton('Collapse all', 'arrows-collapse', collapseAll, false)}
            {!isMobile && headerButton(forceList ? 'Diagram' : 'List', forceList ? 'diagram-2' : 'list-nested', () => setForceList(v => !v), forceList)}
            {headerButton('Terminated', showTerminated ? 'eye-fill' : 'eye-slash', () => setShowTerminated(v => !v), showTerminated)}
          </div>
        </div>
      </div>

      {body}
    </div>
  );
}
